import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Key,
  Phone,
  User,
  RefreshCw,
  Unlock,
  Clock,
  MapPin,
  Search,
  Filter,
  X,
  CheckCircle,
  XCircle,
  Users as UsersIcon,
  KeyRound
} from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import DashboardCard from '../components/DashboardCard';
import Input from '../components/Input';
import Button from '../components/Button';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { PageSkeleton } from '../components/Skeleton';
import { useSocket } from '../contexts/SocketContext';
import { useLockerDrawer } from '../contexts/LockerDrawerContext';
import { calculateExpiryDate, getTimeRemaining, isExpiryApproaching } from '../utils/timezone';

const EMPTY_FORM = { first_name: '', last_name: '', email: '', gym_id: '', phone: '', role: 'user', rfid_tag: '' };

const Users = () => {
  const { socket } = useSocket();
  const { openLocker } = useLockerDrawer();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedUserId = searchParams.get('userId') ? parseInt(searchParams.get('userId')) : null;
  const highlightRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [lockers, setLockers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmUnassign, setConfirmUnassign] = useState(null); // user object
  const [busy, setBusy] = useState(false);
  const [userLogs, setUserLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedLockerId, setSelectedLockerId] = useState(null); // null = random
  const [assignResult, setAssignResult] = useState(null); // { lockerName, pin, userName }
  const [filterLockerStatus, setFilterLockerStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [rfidValidation, setRfidValidation] = useState({ isChecking: false, isAvailable: null, message: '' });
  const [lockerExpiryHours, setLockerExpiryHours] = useState(24);
  const [currentTimezone, setCurrentTimezone] = useState('auto');

  const fetchData = useCallback(async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setRefreshing(true);
      const [usersRes, lockersRes, groupsRes, expiryRes, settingsRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/lockers'),
        axios.get('/api/groups'),
        axios.get('/api/settings/locker-expiry'),
        axios.get('/api/settings')
      ]);
      setUsers(usersRes.data);
      setLockers(lockersRes.data);
      setGroups(groupsRes.data);
      setLockerExpiryHours(expiryRes.data.lockerExpiryHours);
      if (settingsRes.data.systemSettings?.timezone) {
        setCurrentTimezone(settingsRes.data.systemSettings.timezone);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Live updates: keep guests and locker assignments fresh without reloads
  useEffect(() => {
    if (!socket) return undefined;
    const onUserCreated = (u) => setUsers(prev => (prev.some(x => x.id === u.id) ? prev : [...prev, u]));
    const onUserUpdated = (u) => setUsers(prev => prev.map(x => (x.id === u.id ? u : x)));
    const onUserDeleted = (u) => setUsers(prev => prev.filter(x => x.id !== (u.id ?? u)));
    const onLockerUpdated = (l) => setLockers(prev => prev.map(x => (x.id === l.id ? l : x)));
    const onLockerCreated = (l) => setLockers(prev => (prev.some(x => x.id === l.id) ? prev : [...prev, l]));
    socket.on('user-created', onUserCreated);
    socket.on('user-updated', onUserUpdated);
    socket.on('user-deleted', onUserDeleted);
    socket.on('locker-updated', onLockerUpdated);
    socket.on('locker-created', onLockerCreated);
    return () => {
      socket.off('user-created', onUserCreated);
      socket.off('user-updated', onUserUpdated);
      socket.off('user-deleted', onUserDeleted);
      socket.off('locker-updated', onLockerUpdated);
      socket.off('locker-created', onLockerCreated);
    };
  }, [socket]);

  // Deep link from locker cards / global search: scroll to and highlight the guest
  useEffect(() => {
    if (!loading && highlightedUserId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, highlightedUserId, setSearchParams]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/users', formData);
      setUsers(prev => (prev.some(u => u.id === response.data.id) ? prev : [...prev, response.data]));
      setShowAddModal(false);
      setFormData(EMPTY_FORM);
      setRfidValidation({ isChecking: false, isAvailable: null, message: '' });
      toast.success('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.error || 'Failed to create user');
      if (error.response?.data?.error?.includes('RFID tag')) {
        setRfidValidation({ isChecking: false, isAvailable: false, message: error.response.data.error });
      }
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/api/users/${selectedUser.id}`, formData);
      setUsers(users.map(user => (user.id === selectedUser.id ? response.data : user)));
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData(EMPTY_FORM);
      setRfidValidation({ isChecking: false, isAvailable: null, message: '' });
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
      if (error.response?.data?.error?.includes('RFID tag')) {
        setRfidValidation({ isChecking: false, isAvailable: false, message: error.response.data.error });
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await axios.delete(`/api/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const assignableLockers = lockers.filter(locker =>
    locker.status === 'available' &&
    (selectedGroup === 'all' ||
      groups.find(g => g.id === parseInt(selectedGroup))?.locker_ids?.includes(locker.id))
  );

  const handleAssignLocker = async () => {
    try {
      if (assignableLockers.length === 0) {
        toast.error('No available lockers in the selected group');
        return;
      }
      const locker = selectedLockerId
        ? assignableLockers.find(l => l.id === selectedLockerId)
        : assignableLockers[Math.floor(Math.random() * assignableLockers.length)];
      if (!locker) {
        toast.error('Selected locker is no longer available');
        setSelectedLockerId(null);
        return;
      }

      const durationMinutes = lockerExpiryHours * 60;
      const expiryDate = calculateExpiryDate(durationMinutes, currentTimezone);

      const response = await axios.post(`/api/lockers/${locker.id}/assign`, {
        userId: selectedUser.id,
        expiryDate: expiryDate.toISOString()
      });

      const updatedLocker = response.data.locker || response.data;
      setLockers(prev => prev.map(l => (l.id === locker.id ? updatedLocker : l)));
      setUsers(prev => prev.map(u => (u.id === selectedUser.id ? { ...u, locker_id: locker.id } : u)));

      const userName = `${selectedUser.first_name} ${selectedUser.last_name}`;
      setShowAssignModal(false);
      setSelectedGroup('all');
      setSelectedLockerId(null);
      setSelectedUser(null);

      if (response.data.pin) {
        setAssignResult({ lockerName: locker.name, pin: response.data.pin, userName });
      } else {
        toast.success(`Assigned locker ${locker.name} to ${userName}`);
      }
    } catch (error) {
      console.error('Error assigning locker:', error);
      toast.error(error.response?.data?.error || 'Failed to assign locker');
    }
  };

  const handleUnassignLocker = async (user) => {
    setBusy(true);
    try {
      const userLocker = lockers.find(l => l.id === user.locker_id);
      if (!userLocker) {
        toast.error('No locker assigned to this user');
        return;
      }
      const response = await axios.post(`/api/lockers/${userLocker.id}/unassign`);
      const updatedLocker = response.data.locker || response.data;
      setLockers(prev => prev.map(l => (l.id === userLocker.id ? updatedLocker : l)));
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, locker_id: null } : u)));
      toast.success(`Unassigned locker ${userLocker.name} from ${user.first_name} ${user.last_name}`);
    } catch (error) {
      console.error('Error unassigning locker:', error);
      toast.error(error.response?.data?.error || 'Failed to unassign locker');
    } finally {
      setBusy(false);
      setConfirmUnassign(null);
    }
  };

  const handleUnlockLocker = async (userId) => {
    const user = users.find(u => u.id === userId);
    const userLocker = lockers.find(l => l.id === user.locker_id);
    if (!userLocker) {
      toast.error('No locker assigned to this user');
      return;
    }
    try {
      await axios.post(`/api/lockers/${userLocker.id}/command`, { command: 'openlock' });
      toast.success(`Locker ${userLocker.name} unlocked`);
    } catch (error) {
      console.error('Error unlocking locker:', error);
      toast.error(error.response?.data?.error || 'Failed to unlock locker');
    }
  };

  const fetchUserLogs = async (user) => {
    try {
      setSelectedUser(user);
      const response = await axios.get(`/api/users/${user.id}/logs`);
      setUserLogs(response.data);
      setShowLogsModal(true);
    } catch (error) {
      console.error('Error fetching user logs:', error);
      toast.error('Failed to fetch user logs');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      gym_id: user.gym_id || '',
      phone: user.phone || '',
      role: user.role || 'user',
      rfid_tag: user.rfid_tag || '',
    });
    setRfidValidation({ isChecking: false, isAvailable: null, message: '' });
    setShowEditModal(true);
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedGroup('all');
    setSelectedLockerId(null);
    setShowAssignModal(true);
  };

  const getAssignedLocker = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user || !user.locker_id) return null;
    return lockers.find(l => l.id === user.locker_id);
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.gym_id?.toLowerCase().includes(searchLower) ||
      user.rfid_tag?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower);

    const matchesLockerStatus =
      filterLockerStatus === 'all' ||
      (filterLockerStatus === 'assigned' && user.locker_id) ||
      (filterLockerStatus === 'unassigned' && !user.locker_id);

    const matchesRole = filterRole === 'all' || user.role === filterRole;

    let matchesGroup = true;
    if (filterGroup !== 'all') {
      const userLocker = lockers.find(l => l.id === user.locker_id);
      if (userLocker) {
        const group = groups.find(g => g.locker_ids?.includes(userLocker.id));
        matchesGroup = group && group.id === parseInt(filterGroup);
      } else {
        matchesGroup = false;
      }
    }

    return matchesSearch && matchesLockerStatus && matchesRole && matchesGroup;
  });

  const activeFiltersCount = [
    filterLockerStatus !== 'all',
    filterRole !== 'all',
    filterGroup !== 'all'
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterLockerStatus('all');
    setFilterRole('all');
    setFilterGroup('all');
  };

  const validateRfidTag = async (tag) => {
    if (!tag || tag.trim() === '') {
      setRfidValidation({ isChecking: false, isAvailable: null, message: '' });
      return;
    }
    setRfidValidation({ isChecking: true, isAvailable: null, message: 'Checking availability...' });
    try {
      const response = await axios.get(`/api/rfid/check/${tag.trim()}`);
      const isAvailable = response.data.available;
      setRfidValidation({
        isChecking: false,
        isAvailable,
        message: isAvailable ? 'RFID tag is available' : 'RFID tag is already assigned'
      });
    } catch (error) {
      setRfidValidation({ isChecking: false, isAvailable: false, message: 'Error checking RFID tag' });
    }
  };

  const handleRfidTagChange = (e) => {
    const tag = e.target.value;
    setFormData({ ...formData, rfid_tag: tag });
    clearTimeout(rfidValidation.timeout);
    const timeout = setTimeout(() => validateRfidTag(tag), 500);
    setRfidValidation(prev => ({ ...prev, timeout }));
  };

  const rfidFeedback = formData.rfid_tag && rfidValidation.message && (
    <p className={`text-caption mt-1 flex items-center space-x-1 ${
      rfidValidation.isChecking ? 'text-secondary' :
      rfidValidation.isAvailable ? 'text-success-400' : 'text-danger-400'
    }`}>
      {rfidValidation.isChecking ? <RefreshCw className="w-3 h-3 animate-spin" /> :
        rfidValidation.isAvailable ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      <span>{rfidValidation.message}</span>
    </p>
  );

  if (loading) {
    return <PageSkeleton cards={6} />;
  }

  const userForm = (onSubmit, submitLabel, onCancel) => (
    <form onSubmit={onSubmit}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input type="text" placeholder="First Name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
          <Input type="text" placeholder="Last Name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
        </div>
        <Input type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input type="text" placeholder="Gym ID" value={formData.gym_id} onChange={(e) => setFormData({ ...formData, gym_id: e.target.value })} />
          <Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
        </div>
        <select className="input" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <div>
          <Input type="text" placeholder="RFID Tag" value={formData.rfid_tag} onChange={handleRfidTagChange} />
          {rfidFeedback}
        </div>
        <div className="flex space-x-3 pt-2">
          <Button variant="secondary" type="button" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            className="flex-1"
            disabled={rfidValidation.isChecking || rfidValidation.isAvailable === false}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );

  return (
    <div className="p-6">
      <DashboardHeader title="User Management" subtitle="Manage gym members and their locker assignments" />

      {/* Filters Section */}
      <DashboardCard className="mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Filters</h3>
          {activeFiltersCount > 0 && (
            <span className="bg-cyan-500/20 text-cyan-300 text-xs px-2 py-1 rounded-full">
              {activeFiltersCount} active
            </span>
          )}
          {(activeFiltersCount > 0 || searchTerm) && (
            <button
              onClick={clearAllFilters}
              className="ml-auto text-sm text-gray-400 hover:text-white flex items-center space-x-1"
            >
              <X className="w-4 h-4" />
              <span>Clear all</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm text-gray-400 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Name, email, phone, RFID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Locker Status</label>
            <select className="input w-full" value={filterLockerStatus} onChange={(e) => setFilterLockerStatus(e.target.value)}>
              <option value="all">All Users</option>
              <option value="assigned">Has Locker</option>
              <option value="unassigned">No Locker</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Role</label>
            <select className="input w-full" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Locker Group</label>
            <select className="input w-full" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
              <option value="all">All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-dashboard-700 flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Showing <span className="text-white font-medium">{filteredUsers.length}</span> of <span className="text-white font-medium">{users.length}</span> users
          </p>
          <div className="flex items-center space-x-2">
            <Button variant="secondary" className="flex items-center space-x-2" onClick={() => fetchData(false)} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
            <Button variant="primary" className="flex items-center space-x-2" onClick={() => { setFormData(EMPTY_FORM); setRfidValidation({ isChecking: false, isAvailable: null, message: '' }); setShowAddModal(true); }}>
              <Plus className="w-4 h-4" />
              <span>Add User</span>
            </Button>
          </div>
        </div>
      </DashboardCard>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map((user) => {
          const assignedLocker = getAssignedLocker(user.id);
          const isHighlighted = highlightedUserId === user.id;
          return (
            <div
              key={user.id}
              ref={isHighlighted ? highlightRef : undefined}
              className={isHighlighted ? 'rounded-3xl ring-2 ring-primary-400 shadow-glow-blue transition-shadow duration-700' : ''}
            >
            <DashboardCard>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-cyan-200" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">
                      {user.first_name} {user.last_name}
                    </h3>
                    <p className="text-sm text-cyan-200">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="secondary" className="text-cyan-200 hover:text-white" onClick={() => openEditModal(user)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="danger" className="text-danger-400 hover:text-white" onClick={() => handleDeleteUser(user.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2 text-sm">
                  <Key className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-200">Gym ID:</span>
                  <span className="font-medium text-white">{user.gym_id || 'Not set'}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-200">Phone:</span>
                  <span className="font-medium text-white">{user.phone || 'Not set'}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-200">Locker:</span>
                  {assignedLocker ? (
                    <button
                      className="font-medium text-success-400 underline decoration-success-400/40 underline-offset-2 hover:text-success-300"
                      onClick={() => openLocker(assignedLocker.id)}
                      title="Open locker details"
                    >
                      {assignedLocker.name}
                    </button>
                  ) : (
                    <span className="font-medium text-cyan-200">Not assigned</span>
                  )}
                </div>
                {user.valid_until && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    <span className="text-cyan-200">Expires:</span>
                    {(() => {
                      const timeRemaining = getTimeRemaining(user.valid_until);
                      const isApproaching = isExpiryApproaching(user.valid_until);
                      return (
                        <span className={`font-medium ${
                          timeRemaining?.expired ? 'text-red-400' :
                          isApproaching ? 'text-yellow-400' :
                          'text-green-400'
                        }`}>
                          {timeRemaining?.expired ? 'Expired' : `In ${timeRemaining?.text}`}
                        </span>
                      );
                    })()}
                  </div>
                )}
                <div className="flex items-center space-x-2 text-sm">
                  <Key className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-200">RFID Tag:</span>
                  <span className="font-medium text-white">{user.rfid_tag || 'Not set'}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {!assignedLocker && (
                  <Button variant="success" className="flex items-center space-x-1" onClick={() => openAssignModal(user)}>
                    <Key className="w-3 h-3" />
                    <span>Assign Locker</span>
                  </Button>
                )}
                {assignedLocker && (
                  <Button variant="warning" className="flex items-center space-x-1" onClick={() => setConfirmUnassign(user)}>
                    <Unlock className="w-3 h-3" />
                    <span>Unassign</span>
                  </Button>
                )}
                {assignedLocker && (
                  <Button variant="success" className="flex items-center space-x-1" onClick={() => handleUnlockLocker(user.id)}>
                    <Unlock className="w-3 h-3" />
                    <span>Unlock</span>
                  </Button>
                )}
                <Button variant="info" className="flex items-center space-x-1" onClick={() => fetchUserLogs(user)}>
                  <Clock className="w-3 h-3" />
                  <span>Logs</span>
                </Button>
              </div>
            </DashboardCard>
            </div>
          );
        })}
      </div>

      {/* Empty states */}
      {users.length === 0 && (
        <div className="text-center py-16">
          <UsersIcon className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No guests yet</h3>
          <p className="text-cyan-200 mb-6">Add your first guest or assign one from Quick Register</p>
          <Button variant="primary" className="inline-flex items-center space-x-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-5 h-5" />
            <span>Add User</span>
          </Button>
        </div>
      )}
      {users.length > 0 && filteredUsers.length === 0 && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No matches</h3>
          <p className="text-cyan-200 mb-6">No guests match the current search and filters</p>
          <Button variant="secondary" className="inline-flex items-center space-x-2" onClick={clearAllFilters}>
            <X className="w-4 h-4" />
            <span>Clear filters</span>
          </Button>
        </div>
      )}

      {/* Add / Edit User Modals */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} hideDefaultClose>
        <h2 className="text-xl font-bold mb-4 text-white">Add User</h2>
        {userForm(handleAddUser, 'Add User', () => setShowAddModal(false))}
      </Modal>
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} hideDefaultClose>
        <h2 className="text-xl font-bold mb-4 text-white">Edit User</h2>
        {userForm(handleUpdateUser, 'Update User', () => setShowEditModal(false))}
      </Modal>

      {/* Assign Locker Modal: group filter + optional specific locker */}
      <Modal open={showAssignModal} onClose={() => setShowAssignModal(false)} hideDefaultClose>
        <h2 className="text-xl font-bold mb-1 text-white">Assign Locker</h2>
        {selectedUser && (
          <p className="text-sm text-cyan-200 mb-4">
            For {selectedUser.first_name} {selectedUser.last_name} - expires in {lockerExpiryHours}h
          </p>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Locker group</label>
            <select
              className="input w-full"
              value={selectedGroup}
              onChange={(e) => { setSelectedGroup(e.target.value); setSelectedLockerId(null); }}
            >
              <option value="all">All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Locker ({assignableLockers.length} available)
            </label>
            <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto pr-1">
              <button
                type="button"
                onClick={() => setSelectedLockerId(null)}
                className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                  selectedLockerId === null
                    ? 'bg-primary-500/30 text-primary-200 ring-1 ring-primary-400'
                    : 'bg-white/[0.06] text-secondary hover:bg-white/[0.1]'
                }`}
              >
                Random
              </button>
              {assignableLockers.map(locker => (
                <button
                  key={locker.id}
                  type="button"
                  onClick={() => setSelectedLockerId(locker.id)}
                  className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                    selectedLockerId === locker.id
                      ? 'bg-primary-500/30 text-primary-200 ring-1 ring-primary-400'
                      : 'bg-white/[0.06] text-white hover:bg-white/[0.1]'
                  }`}
                >
                  {locker.name}
                </button>
              ))}
            </div>
            {assignableLockers.length === 0 && (
              <p className="text-caption text-danger-400 mt-2">No available lockers in this group</p>
            )}
          </div>
          <div className="flex space-x-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button variant="success" className="flex-1" onClick={handleAssignLocker} disabled={assignableLockers.length === 0}>
              {selectedLockerId ? `Assign ${assignableLockers.find(l => l.id === selectedLockerId)?.name || ''}` : 'Assign Random'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assignment result with the guest's PIN */}
      <Modal open={!!assignResult} onClose={() => setAssignResult(null)} hideDefaultClose>
        {assignResult && (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-success-500/15 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-success-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Locker {assignResult.lockerName} assigned</h2>
            <p className="text-sm text-cyan-200 mb-5">{assignResult.userName}</p>
            <div className="material-thin rounded-2xl p-5 mb-2">
              <p className="text-footnote text-label mb-1">Phone-unlock PIN</p>
              <p className="text-5xl font-extrabold tracking-[0.3em] text-primary-300" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {assignResult.pin}
              </p>
            </div>
            <p className="text-caption text-muted mb-5">Show this to the guest now - it is displayed only once.</p>
            <Button variant="primary" className="w-full" onClick={() => setAssignResult(null)}>Done</Button>
          </div>
        )}
      </Modal>

      {/* User Logs Modal */}
      {showLogsModal && (
        <Modal open={showLogsModal} onClose={() => setShowLogsModal(false)}>
          <h2 className="text-xl font-semibold text-white mb-4">
            Activity Logs - {selectedUser?.first_name} {selectedUser?.last_name}
          </h2>
          {userLogs.length > 0 ? (
            <div className="space-y-3">
              {userLogs.map((log) => (
                <div key={log.id} className="material-thin rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{log.action}</p>
                      <p className="text-sm text-secondary">
                        {log.details?.locker_name || 'Unknown locker'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">
                        {new Date(log.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-muted">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  {log.details?.start_time && log.details?.end_time && (
                    <p className="mt-2 text-sm text-secondary">
                      Duration: {new Date(log.details.start_time).toLocaleString()} - {new Date(log.details.end_time).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary text-center py-8">No activity logs found for this user.</p>
          )}
        </Modal>
      )}

      {/* Unassign confirmation */}
      <ConfirmDialog
        open={!!confirmUnassign}
        onClose={() => setConfirmUnassign(null)}
        onConfirm={() => handleUnassignLocker(confirmUnassign)}
        title={confirmUnassign ? `Unassign ${confirmUnassign.first_name}'s locker?` : ''}
        message="The guest loses access and the locker opens for clear-out."
        confirmLabel="Unassign"
        tone="danger"
        busy={busy}
      />
    </div>
  );
};

export default Users;
